import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { customFieldService } from '../../services/custom-field.service'
import { documentTypeService } from '../../services/document-type.service'
import { PermissionAction, PermissionType } from '../../services/permissions.service'
import { toastService } from '../../services/toast.service'
import { CustomField, DocumentTemplate, DocumentType } from '../../types'
import IfPermissions from '../common/IfPermissions'

export type WordTemplateEditorMode = 'create' | 'edit' | 'reupload'

interface WordTemplateEditorModalProps {
  documentType: DocumentType
  show: boolean
  templateId: number | null
  mode: WordTemplateEditorMode
  onClose: () => void
  onSuccess: () => void
}

interface MappingRow {
  fieldId: number | null
  tag: string
}

const buildMappingRows = (template: DocumentTemplate | null): MappingRow[] => {
  if (!template) return []
  const tags = template.tags || []
  return Object.entries(template.mappings || {})
    .filter(([tag]) => tags.includes(tag))
    .sort(([firstTag], [secondTag]) => tags.indexOf(firstTag) - tags.indexOf(secondTag))
    .map(([tag, fieldId]) => ({ tag, fieldId: Number(fieldId) }))
}

const buildInitialMappingRows = (template: DocumentTemplate | null): MappingRow[] => {
  const mappedRows = buildMappingRows(template)
  if (mappedRows.length > 0) return mappedRows
  const firstTag = template?.tags?.[0]
  return firstTag ? [{ tag: firstTag, fieldId: null }] : []
}

const formatFileSize = (size?: number | null): string => {
  if (!size || size <= 0) return ''
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

const WordTemplateEditorModal: React.FC<WordTemplateEditorModalProps> = ({
  documentType,
  show,
  templateId,
  mode,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const tx = (key: string, fallback: string) => {
    const value = t(key)
    return !value || value === key ? fallback : value
  }
  const [template, setTemplate] = useState<DocumentTemplate | null>(null)
  const [documentTypeDetails, setDocumentTypeDetails] = useState<DocumentType>(documentType)
  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [mappings, setMappings] = useState<Record<string, number>>({})
  const [mappingRows, setMappingRows] = useState<MappingRow[]>([])
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const documentTypeFieldIds = useMemo(
    () => new Set([
      ...(documentTypeDetails.custom_fields || []),
      ...(documentTypeDetails.custom_field_configs || []).map((config) => config.custom_field),
    ]),
    [documentTypeDetails.custom_fields, documentTypeDetails.custom_field_configs],
  )

  const availableFields = useMemo(
    () => customFields.filter(
      (field) => documentTypeFieldIds.has(field.id) && field.data_type !== 'separator' && field.data_type !== 'counter',
    ),
    [customFields, documentTypeFieldIds],
  )
  const fieldsById = useMemo(() => new Map(availableFields.map((field) => [field.id, field])), [availableFields])

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const [wordTemplates, fields, typeDetails] = await Promise.all([
        documentTypeService.getWordTemplates(documentType.id),
        customFieldService.listAll(),
        documentTypeService.getDocumentType(documentType.id).catch(() => documentType),
      ])
      const selected = templateId === null ? null : wordTemplates.find((item) => item.id === templateId) || null
      setDocumentTypeDetails(typeDetails)
      setTemplate(selected)
      setCustomFields(fields.results || [])
      setMappings(selected?.mappings || {})
      setMappingRows(buildInitialMappingRows(selected))
      setFile(null)
      if (templateId !== null && !selected) {
        setError(tx('template_not_found', 'Šablon više ne postoji.'))
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || tx('error_loading_data', 'Greška pri učitavanju podataka.'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (show) void load()
    // The modal reloads whenever a different template is selected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, documentType.id, templateId])

  const mappingsFromRows = (rows: MappingRow[]) => {
    const templateTags = new Set(template?.tags || [])
    const staleMappings = Object.fromEntries(
      Object.entries(mappings).filter(([tag]) => !templateTags.has(tag)),
    )
    rows.forEach((row) => {
      if (row.tag && row.fieldId) staleMappings[row.tag] = row.fieldId
    })
    return staleMappings
  }

  const updateMappingRow = (index: number, patch: Partial<MappingRow>) => {
    const nextRows = mappingRows.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row)
    setMappingRows(nextRows)
    setMappings(mappingsFromRows(nextRows))
  }

  const addMapping = () => {
    if (!template) return
    const usedTags = new Set(mappingRows.map((row) => row.tag).filter(Boolean))
    const usedFields = new Set(mappingRows.map((row) => row.fieldId).filter(Boolean))
    const nextRow: MappingRow = {
      tag: (template.tags || []).find((tag) => !usedTags.has(tag)) || '',
      fieldId: availableFields.find((field) => !usedFields.has(field.id))?.id || null,
    }
    const nextRows = [...mappingRows, nextRow]
    setMappingRows(nextRows)
    setMappings(mappingsFromRows(nextRows))
  }

  const removeMapping = (index: number) => {
    const nextRows = mappingRows.filter((_row, rowIndex) => rowIndex !== index)
    setMappingRows(nextRows)
    setMappings(mappingsFromRows(nextRows))
  }

  const save = async () => {
    if (!file && !template) {
      setError(tx('template_file_required', 'Izaberite .docx fajl za novi šablon.'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const isNewTemplate = template === null
      const saved = file
        ? await documentTypeService.uploadWordTemplate(documentType.id, file, {
            templateId: template?.id,
            mappings,
          })
        : await documentTypeService.updateWordTemplate(documentType.id, template!.id, { mappings })
      setTemplate(saved)
      setMappings(saved.mappings || {})
      setMappingRows(buildInitialMappingRows(saved))
      setFile(null)
      onSuccess()
      toastService.showSuccess(
        isNewTemplate
          ? tx('template_uploaded_map_tags', 'Šablon je dodat. Dodajte mapiranja atributa i sačuvajte šablon.')
          : tx('template_mappings_saved', 'Šablon i mapiranja atributa su uspešno sačuvani.'),
      )
    } catch (err: any) {
      setError(err?.response?.data?.error || tx('error_saving', 'Greška pri čuvanju šablona.'))
    } finally {
      setSaving(false)
    }
  }

  const refresh = async () => {
    if (!template) return
    setRefreshing(true)
    setError(null)
    try {
      const refreshed = await documentTypeService.refreshWordTemplate(documentType.id, template.id)
      setTemplate(refreshed)
      setMappings(refreshed.mappings || {})
      setMappingRows(buildInitialMappingRows(refreshed))
      onSuccess()
      toastService.showSuccess(tx('template_tags_refreshed', 'Tagovi su osveženi.'))
    } catch (err: any) {
      setError(err?.response?.data?.error || tx('error_loading_data', 'Greška pri osvežavanju tagova.'))
    } finally {
      setRefreshing(false)
    }
  }

  const title = {
    create: tx('add_document_template', 'Dodaj šablon dokumenta'),
    edit: tx('edit_document_template', 'Izmeni šablon dokumenta'),
    reupload: tx('reupload_document_template', 'Ponovo učitaj šablon dokumenta'),
  }[mode]

  const selectedFileName = file?.name || template?.original_filename || ''
  const selectedFileSize = file?.size || template?.file_size
  const mappedAttributeCount = template
    ? Object.entries(mappings).filter(([tag]) => (template.tags || []).includes(tag)).length
    : 0

  const selectFile = (selectedFile: File | undefined) => {
    if (!selectedFile) return
    if (!selectedFile.name.toLowerCase().endsWith('.docx')) {
      setError(tx('template_file_type_invalid', 'Dozvoljeni su samo Word .docx fajlovi.'))
      return
    }
    setError(null)
    setFile(selectedFile)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    selectFile(event.target.files?.[0])
    event.target.value = ''
  }

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    selectFile(event.dataTransfer.files?.[0])
  }

  return (
    <Modal show={show} onHide={onClose} size="xl" centered scrollable>
      <Modal.Header>
        <Button variant="link" className="text-dark p-0 me-3" onClick={onClose} aria-label={tx('back', 'Nazad')}>
          <i className="bi bi-arrow-left fs-4" />
        </Button>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-4">
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>
        ) : (
          <>
            {mode === 'reupload' && template && (
              <Alert variant="info">
                {tx('template_reupload_help', 'Izaberite novu verziju Word fajla. Postojeća mapiranja će ostati sačuvana za tagove koji nisu promenjeni.')}
              </Alert>
            )}

            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">{tx('template_document', 'Dokument šablona')}</Form.Label>
              {template && !file && (
                <div className="border rounded p-3 d-flex align-items-center justify-content-between gap-3">
                  <div className="d-flex align-items-center min-width-0">
                    <Button
                      type="button"
                      variant="light"
                      className="word-template-reupload-action border rounded me-2"
                      title={tx('reupload_template', 'Ponovo učitaj šablon')}
                      aria-label={tx('reupload_template', 'Ponovo učitaj šablon')}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={saving}
                    >
                      <i className="bi bi-upload text-primary" />
                    </Button>
                    <div className="rounded d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: 48, height: 48, backgroundColor: '#e8f7ed' }}>
                      <i className="bi bi-file-earmark-text text-success fs-4" />
                    </div>
                    <div className="min-width-0">
                      <div className="fw-semibold text-truncate">{selectedFileName}</div>
                      <div className="text-muted small">{formatFileSize(selectedFileSize)}</div>
                    </div>
                  </div>
                  <span className="badge rounded-pill text-success-emphasis" style={{ backgroundColor: '#e8f7ed' }}>
                    {template.tags.length > 0
                      ? `${tx('template_tags_loaded', 'Tagovi učitani')} (${template.tags.length})`
                      : tx('no_template_tags', 'Tagovi nisu pronađeni')}
                  </span>
                </div>
              )}
              {file && (
                <div className="border rounded p-3 d-flex align-items-center justify-content-between gap-3">
                  <div className="d-flex align-items-center min-width-0">
                    <Button
                      type="button"
                      variant="light"
                      className="word-template-reupload-action border rounded me-2"
                      title={tx('reupload_template', 'Ponovo učitaj šablon')}
                      aria-label={tx('reupload_template', 'Ponovo učitaj šablon')}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={saving}
                    >
                      <i className="bi bi-upload text-primary" />
                    </Button>
                    <div className="rounded d-flex align-items-center justify-content-center me-3 flex-shrink-0" style={{ width: 48, height: 48, backgroundColor: '#e8f7ed' }}>
                      <i className="bi bi-file-earmark-text text-success fs-4" />
                    </div>
                    <div className="min-width-0">
                      <div className="fw-semibold text-truncate">{file.name}</div>
                      <div className="text-muted small">{formatFileSize(file.size)}</div>
                    </div>
                  </div>
                  <span className="badge rounded-pill text-warning-emphasis" style={{ backgroundColor: '#fff3d9' }}>
                    {saving
                      ? tx('loading_template_tags', 'Učitavanje tagova...')
                      : tx('template_ready_for_upload', 'Spremno za učitavanje')}
                  </span>
                </div>
              )}
              {!template && !file && (
                <div
                  className="word-template-upload-dropzone d-flex flex-column align-items-center justify-content-center text-center px-3"
                  role="button"
                  tabIndex={0}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') fileInputRef.current?.click()
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={handleFileDrop}
                >
                  <i className="bi bi-upload text-primary fs-1 mb-2" />
                  <div className="fw-semibold fs-5">{tx('drop_word_template', 'Prevucite Word dokument ovde ili kliknite da izaberete')}</div>
                  <div className="text-muted mt-1">{tx('supported_docx_format', 'Podržan format: .docx')}</div>
                </div>
              )}
              <Form.Control
                ref={fileInputRef}
                id="word-template-file"
                className="d-none"
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileChange}
              />
            </Form.Group>

            {template ? (
              <>
                <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                  <div>
                    <h5 className="mb-2">{tx('template_attribute_mapping_title', 'Mapiranje atributa i tagova')}</h5>
                    <p className="text-muted mb-0">
                      {tx('template_attribute_mapping_help', 'Povežite atribute tipa dokumenta sa tagovima pronađenim u Word šablonu. Prilikom unosa atributa u DOCDOT, vrednosti će se automatski upisati na povezano mesto u dokumentu.')}
                    </p>
                  </div>
                  <Button variant="outline-primary" className="text-nowrap" onClick={() => void refresh()} disabled={refreshing || saving}>
                    {refreshing ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-arrow-clockwise me-2" />}
                    {tx('refresh_tags', 'Osveži tagove')}
                  </Button>
                </div>

                <div className="border rounded overflow-hidden mt-3">
                  <Table responsive className="mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th className="text-uppercase text-muted small px-3 py-3">{tx('system_attribute', 'Atribut sistema')}</th>
                        <th style={{ width: 45 }} />
                        <th className="text-uppercase text-muted small px-3 py-3">{tx('document_tag', 'Tag u dokumentu')}</th>
                        <th style={{ width: 58 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {mappingRows.map((row, index) => {
                        const usedFieldIds = new Set(mappingRows.filter((_item, rowIndex) => rowIndex !== index).map((item) => item.fieldId).filter(Boolean))
                        const usedTags = new Set(mappingRows.filter((_item, rowIndex) => rowIndex !== index).map((item) => item.tag).filter(Boolean))
                        return (
                          <tr key={`${index}-${row.tag}`}>
                            <td className="px-3 py-3">
                              <Form.Select value={row.fieldId || ''} onChange={(event) => updateMappingRow(index, { fieldId: event.target.value ? Number(event.target.value) : null })}>
                                <option value="">{tx('select_attribute', 'Izaberi atribut')}</option>
                                {availableFields.filter((field) => !usedFieldIds.has(field.id) || field.id === row.fieldId).map((field) => (
                                  <option key={field.id} value={field.id}>{field.name}</option>
                                ))}
                              </Form.Select>
                            </td>
                            <td className="text-center text-muted fs-5">→</td>
                            <td className="px-3 py-3">
                              <Form.Select value={row.tag} onChange={(event) => updateMappingRow(index, { tag: event.target.value })}>
                                <option value="">{tx('select_tag', 'Izaberi tag')}</option>
                                {(template.tags || []).filter((tag) => !usedTags.has(tag) || tag === row.tag).map((tag) => (
                                  <option key={tag} value={tag}>{`{{${tag}}}`}</option>
                                ))}
                              </Form.Select>
                            </td>
                            <td className="text-center px-2">
                              <Button variant="light" className="border rounded text-danger" onClick={() => removeMapping(index)} title={tx('remove_mapping', 'Ukloni mapiranje')} aria-label={tx('remove_mapping', 'Ukloni mapiranje')}>
                                <i className="bi bi-x-lg" />
                              </Button>
                            </td>
                          </tr>
                        )
                      })}
                      {mappingRows.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-muted text-center py-4">{tx('no_mappings', 'Još uvek nema dodatih mapiranja.')}</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                <Button variant="link" className="px-0 mt-3 fw-semibold" onClick={addMapping} disabled={!template.tags.length}>
                  <i className="bi bi-plus-lg me-2" />
                  {tx('add_mapping', 'Dodaj mapiranje')}
                </Button>

                {availableFields.length === 0 && (
                  <Alert variant="info" className="mt-2 mb-0">
                    {tx('no_template_attributes', 'Za mapiranje prvo dodajte atribute ovom tipu dokumenta.')}
                  </Alert>
                )}

                {template.stale_mappings?.length > 0 && (
                  <Alert variant="warning" className="mt-3 mb-0">
                    {tx('stale_template_mappings', 'Neka prethodna mapiranja više ne postoje u fajlu')}: {template.stale_mappings.join(', ')}
                  </Alert>
                )}
                {Object.values(mappings).some((fieldId) => !fieldsById.has(Number(fieldId))) && (
                  <Alert variant="warning" className="mt-3 mb-0">
                    {tx('invalid_template_attribute', 'Neko mapiranje više nije dostupno za ovaj tip dokumenta.')}
                  </Alert>
                )}
                <div className="text-muted small mt-2">{mappedAttributeCount} {tx('template_mapped_attributes', 'povezanih atributa')}</div>
              </>
            ) : (
              <Alert variant="info" className="mb-0">
                <div className="fw-semibold mb-1">{tx('upload_template_help_title', 'Otpremite Word šablon')}</div>
                {tx('upload_template_help', 'Sačuvajte .docx fajl da biste učitali njegove tagove, a zatim povežite atribute dokumenta sa tagovima.')}
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>{t('cancel')}</Button>
        <IfPermissions action={PermissionAction.Change} type={PermissionType.DocumentType}>
          <Button variant="primary" onClick={() => void save()} disabled={loading || saving || refreshing}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            {tx('save_template', 'Sačuvaj šablon')}
          </Button>
        </IfPermissions>
      </Modal.Footer>
    </Modal>
  )
}

export default WordTemplateEditorModal
